/** Look-ups for deputies: active civil papers, evictions, upcoming court, on-call magistrate, phone numbers, equipment requests. */
import { useEffect, useState } from 'react';
import { api } from '../shared/api.ts';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, when } from './common.tsx';
import { useRouter } from '../shared/router.tsx';

const tel = (p: string) => `tel:${p.replace(/[^\d+]/g, '')}`;
const STATUS: Record<string, string> = { RECEIVED: 'Received', INTAKE: 'Intake', REVIEWED: 'Reviewed', READY: 'Waiting for a deputy', ASSIGNED: 'Assigned', IN_SERVICE: 'Being served', PENDING_REVIEW: 'Pending review' };

export function ActivePapersSearch() {
  const start = new URLSearchParams(location.search).get('q') ?? '';
  const [q, setQ] = useState(start);
  const [query, setQuery] = useState(start.trim());
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);
  const r = useLoad(() => (query.length >= 2 ? api.get<any>(`/api/papers/active-search?q=${encodeURIComponent(query)}`) : Promise.resolve(null)), [query]);
  return (
    <div className="ops stack">
      <PageHeader crumb="Civil papers" title="Active civil papers" sub="Look up a name or address to see if civil papers are waiting to be served. Look-up only: the civil process unit serves them." />
      <input type="search" className="rp-search big" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Last name, first name, or street address" aria-label="Search active civil papers" autoFocus />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : query.length < 2 ? (
        <EmptyState icon="search" title="Search active civil papers">
          Type at least two letters of a name or an address.
        </EmptyState>
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.papers.length ? (
        <EmptyState icon="check" title="No active civil papers">
          Nothing open for “{query}”.
        </EmptyState>
      ) : (
        <div className="stack">
          {r.data.papers.map((p: any) => (
            <div key={p.id} className="card cp-row">
              <div>
                <strong>{p.defendant}</strong>
                <div className="small">{p.address}</div>
                <div className="small muted">
                  {p.type} · {p.number}
                  {p.courtDate ? ` · court ${day(p.courtDate)}` : ''}
                </div>
              </div>
              <div className="cp-side">
                <Badge tone={p.onHold ? 'warn' : 'info'} dot>
                  {p.onHold ? 'On hold' : STATUS[p.status] ?? p.status}
                </Badge>
                <span className="small muted">{p.deputy ? `Civil deputy: ${p.deputy}` : 'Not assigned yet'}</span>
              </div>
            </div>
          ))}
          <p className="small muted">If you make contact, let the civil deputy (or the civil office) know where the person is.</p>
        </div>
      )}
    </div>
  );
}

const STAGE: Record<string, string> = { waiting: 'Served · 24-hour wait', ready: 'Served · waiting to be scheduled', scheduled: 'Set-out scheduled', done: 'Done', to_serve: 'Served' };
const OUTCOME: Record<string, string> = { executed: 'Set-out done', already_vacated: 'Tenant had already moved out', cancelled: 'Cancelled' };

export function EvictionsPage() {
  const r = useLoad(() => api.get<any>('/api/evictions'), []);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const cards: any[] = r.data.cards;
  const groups = [
    { title: 'Set-outs scheduled', rows: cards.filter((c) => c.stage === 'scheduled') },
    { title: 'Served, waiting on a set-out', rows: cards.filter((c) => c.stage === 'waiting' || c.stage === 'ready') },
    { title: 'Done in the last 30 days', rows: cards.filter((c) => c.stage === 'done') },
  ];
  return (
    <div className="ops stack">
      <PageHeader crumb="Civil papers" title="Evictions" sub="Writs of ejectment that have been served, set-outs on the calendar, and the last 30 days — so you know the history when you get a call at the address." />
      {groups.map((g) => (
        <Card key={g.title} title={`${g.title} (${g.rows.length})`}>
          {!g.rows.length ? (
            <p className="muted">None.</p>
          ) : (
            <ul className="ev-list">
              {g.rows.map((c) => (
                <li key={c.paperId}>
                  <div>
                    <strong>{c.address}</strong>
                    <div className="small">
                      {c.defendant}
                      {c.plaintiff ? ` · landlord ${c.plaintiff}` : ''}
                    </div>
                  </div>
                  <div className="ev-side small">
                    {c.setOut?.status === 'scheduled' ? (
                      <>
                        <strong>{when(c.setOut.scheduledFor, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</strong>
                        <span className="muted">{c.setOut.deputies.map((d: any) => d.name).join(', ')}</span>
                      </>
                    ) : c.stage === 'done' ? (
                      <>
                        <span>{c.setOut ? OUTCOME[c.setOut.outcome] ?? (c.setOut.status === 'cancelled' ? `Cancelled${c.setOut.cancelReason ? `: ${c.setOut.cancelReason}` : ''}` : 'Done') : c.doneReason ?? 'Done'}</span>
                        <span className="muted">{c.setOut?.completedAt ? when(c.setOut.completedAt) : ''}</span>
                      </>
                    ) : (
                      <>
                        <span>{STAGE[c.stage]}</span>
                        {c.servedAt && <span className="muted">Served {when(c.servedAt)}</span>}
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ))}
    </div>
  );
}

const ROLE: Record<string, string> = { security: 'Court security', testifying: 'Testifying', transport: 'Transport', other: 'Court' };
const KIND: Record<string, string> = { trial: 'Trial', jury_trial: 'Jury trial', bench_trial: 'Bench trial', preliminary: 'Preliminary hearing', bond: 'Bond hearing', hearing: 'Hearing', security: 'Court security' };

export function UpcomingCourt() {
  const { navigate } = useRouter();
  const today = new Date().toLocaleDateString('en-CA');
  const to = new Date(Date.now() + 90 * 86_400_000).toLocaleDateString('en-CA');
  const r = useLoad(() => api.get<any>(`/api/court/mine?from=${today}&to=${to}`), []);
  return (
    <div className="ops stack">
      <PageHeader crumb="Court" title="Upcoming cases" sub="Your court dates for the next three months: testifying, court security and transport." />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.events.length ? (
        <EmptyState icon="calendar" title="No court dates coming up" />
      ) : (
        <div className="stack">
          {r.data.events.map((e: any) => (
            <button key={`${e.id}-${e.role}`} type="button" className="card uc-row" onClick={() => navigate(at(`/court/${e.id}`))}>
              <span className="uc-when">
                <strong>{new Date(e.starts_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</strong>
                <span className="small">{new Date(e.starts_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
              </span>
              <span>
                <strong>
                  {ROLE[e.role] ?? 'Court'} · {KIND[e.kind] ?? e.kind}
                </strong>
                <div className="small">
                  {[e.court_name, e.courtroom].filter(Boolean).join(', ')}
                  {e.court_case_number ? ` · ${e.court_case_number}` : ''}
                  {e.defendant ? ` · ${e.defendant}` : ''}
                </div>
              </span>
              {e.status === 'continued' && <Badge tone="warn">Continued</Badge>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const localInput = (d: Date) => {
  const x = new Date(+d - d.getTimezoneOffset() * 60_000);
  return x.toISOString().slice(0, 16);
};

export function OnCallPage() {
  const r = useLoad(() => api.get<any>('/api/on-call'), []);
  const a = useAction();
  const start = new Date();
  start.setHours(17, 0, 0, 0);
  const [f, setF] = useState({ name: '', phone: '', court: '', notes: '', startsAt: localInput(start), endsAt: localInput(new Date(+start + 7 * 86_400_000 - 9 * 3_600_000)) });
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const d = r.data;
  const row = (o: any, big = false) => (
    <div key={o.id} className={`card oc-row ${big ? 'big' : ''}`}>
      <div>
        <strong>{o.name}</strong>
        <div className="small muted">
          {when(o.starts_at)} – {when(o.ends_at)}
          {o.court ? ` · ${o.court}` : ''}
        </div>
        {o.notes && <div className="small">{o.notes}</div>}
      </div>
      <div className="row gap-sm">
        {o.phone && (
          <a className="btn primary" href={tel(o.phone)}>
            Call {o.phone}
          </a>
        )}
        {d.canEdit && (
          <button className="btn ghost sm" disabled={a.busy} onClick={() => window.confirm(`Remove ${o.name} from the on-call list?`) && a.run(async () => (await api.post(`/api/on-call/${o.id}/delete`), r.reload()))}>
            Remove
          </button>
        )}
      </div>
    </div>
  );
  return (
    <div className="ops stack">
      <PageHeader crumb="Court" title="On-call magistrate" sub="Who to call after hours for warrants and bond." />
      <ErrorBox error={a.error} />
      <h2 className="dh-h">On call now</h2>
      {d.now.length ? d.now.map((o: any) => row(o, true)) : <Notice tone="warn">No one is listed as on call right now. Check with your supervisor or dispatch.</Notice>}
      {d.upcoming.length > 0 && (
        <>
          <h2 className="dh-h">Coming up</h2>
          {d.upcoming.map((o: any) => row(o))}
        </>
      )}
      {d.canEdit && (
        <Card title="Add to the on-call list">
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              a.run(async () => {
                await api.post('/api/on-call', { ...f, startsAt: new Date(f.startsAt).toISOString(), endsAt: new Date(f.endsAt).toISOString() });
                setF({ ...f, name: '', phone: '', notes: '' });
                r.reload();
              });
            }}
          >
            <div className="grid-3">
              <label>
                Magistrate
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required placeholder="Judge …" />
              </label>
              <label>
                Phone
                <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} inputMode="tel" />
              </label>
              <label>
                Court
                <input value={f.court} onChange={(e) => setF({ ...f, court: e.target.value })} />
              </label>
              <label>
                From
                <input type="datetime-local" value={f.startsAt} onChange={(e) => setF({ ...f, startsAt: e.target.value })} required />
              </label>
              <label>
                Until
                <input type="datetime-local" value={f.endsAt} onChange={(e) => setF({ ...f, endsAt: e.target.value })} required />
              </label>
              <label>
                Notes
                <input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="e.g. text first after 11 pm" />
              </label>
            </div>
            <div>
              <button className="btn primary" disabled={a.busy}>
                Add
              </button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}

export function PhoneNumbersPage() {
  const r = useLoad(() => api.get<any>('/api/phone-numbers'), []);
  const a = useAction();
  const [f, setF] = useState({ id: '', category: 'Dispatch & emergency', name: '', phone: '', notes: '' });
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const d = r.data;
  const cats = [...new Set<string>(d.numbers.map((n: any) => n.category))];
  return (
    <div className="ops stack">
      <PageHeader crumb="Resources" title="Important phone numbers" sub="Numbers the office keeps for you. Tap one to call." />
      <ErrorBox error={a.error} />
      {d.office.phone && (
        <Card title="The office">
          <div className="pn-row">
            <span>Sheriff’s office{d.office.address ? <span className="small muted"> · {d.office.address}</span> : null}</span>
            <a href={tel(d.office.phone)}>{d.office.phone}</a>
          </div>
        </Card>
      )}
      {!d.numbers.length && <EmptyState icon="phone" title="No numbers yet">{d.canEdit ? 'Add the numbers deputies call most below.' : 'An administrator adds them.'}</EmptyState>}
      {cats.map((c) => (
        <Card key={c} title={c}>
          {d.numbers
            .filter((n: any) => n.category === c)
            .map((n: any) => (
              <div key={n.id} className="pn-row">
                <span>
                  {n.name}
                  {n.notes && <span className="small muted"> · {n.notes}</span>}
                </span>
                <span className="row gap-sm">
                  <a href={tel(n.phone)}>{n.phone}</a>
                  {d.canEdit && (
                    <>
                      <button className="btn ghost sm" onClick={() => setF({ id: n.id, category: n.category, name: n.name, phone: n.phone, notes: n.notes ?? '' })}>
                        Edit
                      </button>
                      <button className="btn ghost sm" disabled={a.busy} onClick={() => window.confirm(`Remove ${n.name}?`) && a.run(async () => (await api.post(`/api/phone-numbers/${n.id}/delete`), r.reload()))}>
                        Remove
                      </button>
                    </>
                  )}
                </span>
              </div>
            ))}
        </Card>
      ))}
      {d.canEdit && (
        <Card title={f.id ? 'Edit number' : 'Add a number'}>
          <form
            className="grid-4"
            onSubmit={(e) => {
              e.preventDefault();
              a.run(async () => {
                await api.post('/api/phone-numbers', { ...f, id: f.id || undefined });
                setF({ id: '', category: f.category, name: '', phone: '', notes: '' });
                r.reload();
              });
            }}
          >
            <label>
              Group
              <input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} list="pn-cats" required />
              <datalist id="pn-cats">
                {[...new Set(['Dispatch & emergency', 'Courts & magistrates', 'Jail', 'Other agencies', 'Services', ...cats])].map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </label>
            <label>
              Name
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
            </label>
            <label>
              Phone
              <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} required inputMode="tel" />
            </label>
            <label>
              Note
              <input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
            </label>
            <div className="row gap-sm">
              <button className="btn primary" disabled={a.busy}>
                {f.id ? 'Save' : 'Add'}
              </button>
              {f.id && (
                <button type="button" className="btn ghost" onClick={() => setF({ id: '', category: f.category, name: '', phone: '', notes: '' })}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}

export function EquipmentRequestPage() {
  const [f, setF] = useState({ item: '', reason: '', urgent: false });
  const [sent, setSent] = useState(false);
  const a = useAction();
  return (
    <div className="ops stack">
      <PageHeader crumb="Equipment" title="Request equipment" sub="Goes to the quartermaster as a message that needs a reply. You’ll see the answer in Messages." />
      {sent ? (
        <>
          <Notice tone="ok">Sent. The quartermaster will answer in Messages.</Notice>
          <div>
            <button className="btn" onClick={() => (setSent(false), setF({ item: '', reason: '', urgent: false }))}>
              Request something else
            </button>
          </div>
        </>
      ) : (
        <form
          className="card stack"
          onSubmit={(e) => {
            e.preventDefault();
            a.run(async () => {
              await api.post('/api/me/equipment-request', f);
              setSent(true);
            });
          }}
        >
          <label>
            What do you need?
            <input value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} required placeholder="e.g. radio battery, vest carrier (size L), flashlight" />
          </label>
          <label>
            Why
            <textarea rows={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} required placeholder="Broken, lost, worn out, new assignment…" />
          </label>
          <label className="check">
            <input type="checkbox" checked={f.urgent} onChange={(e) => setF({ ...f, urgent: e.target.checked })} /> Urgent: I can’t work my shift without it
          </label>
          <ErrorBox error={a.error} />
          <div>
            <button className="btn primary" disabled={a.busy}>
              Send request
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
